import React, { useCallback, useEffect, useState } from "react";

import { assignCreditsToPartner, getAssignableFarmFieldMethods, getPartnerCreditAssignments } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import { AdminTable, Col, InsightCard, PageSpin, Row, Switch, Message as message } from "components";

export const AssignMethodCreditsToSinglePartner = ({ partner }) => {
  const signal = useCancelAsyncOnUnmount()();
  const [farmFieldMethods, setFarmFieldMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [assignedCredits, setAssignedCredits] = useState(0);
  const [isInsetting, setIsInsetting] = useState(false);

  const loadFarmFieldMethods = useCallback(() => {
    getAssignableFarmFieldMethods({
      page,
      signal,
    }).then((res) => {
      setFarmFieldMethods(res.methods.filter((fieldMethod) => fieldMethod.assignableCredits > 0));
      setLoading(false);

      if (res.count > page * 250 && res.count > 250) {
        setPage(page + 1);
      }
    });
  }, [page, signal]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCreditAssignments = useCallback(() => {
    setLoading(true);
    getPartnerCreditAssignments({
      id: partner.id,
      signal,
    })
      .then((res) => {
        setAssignedCredits(res.reduce((a, b) => a + b.credits, 0));
      })
      .finally(() => setLoading(false));
  }, [partner, signal]);

  const complete = (v) => {
    if (isInsetting && partner.id !== v.farmPartnerId) {
      message({
        content: "You can only assign credits as insetting, when the farmer is assigned to the partner",
        icon: "gremlin",
      });
      return;
    }

    setLoadingCredit(true);
    setAssignedCredits(assignedCredits + v.assignableCredits);

    assignCreditsToPartner({
      credits: v.assignableCredits,
      farmFieldMethodOrModuleId: v.id,
      partnerId: partner.id,
      creditType: isInsetting ? "Insetting" : "Offsetting",
      farmId: v.farmId,
      fieldId: v.fieldId,
      completed: v.completed,
      id: v.id,
      year: v.year,
      type: "MP",
      signal,
    })
      .then(() => {
        setLoadingCredit(false);
        loadFarmFieldMethods();
        message({
          content: "The gremlins accepted your sacrifice and will consume it! Nom nom nom",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadFarmFieldMethods();
  }, [loadFarmFieldMethods, getCreditAssignments, page]);

  return (
    <>
      <Row>
        <Col sm={8}>
          <InsightCard label="Partner credit purchases" loading={loadingCredit}>
            {partner.purchasedCredits}
          </InsightCard>
        </Col>
        <Col sm={8}>
          <InsightCard label="Assigned MP credits" loading={loadingCredit}>
            {+assignedCredits.toFixed(4)}
          </InsightCard>
        </Col>
        <Col sm={8}>
          <InsightCard label="Remaining" loading={loadingCredit}>
            {+(partner.purchasedCredits - assignedCredits).toFixed(4)}
          </InsightCard>
        </Col>
      </Row>
      <Row>
        <Col sm={12}>
          <p className="paragraph-s red-text">
            Partial credit assignments are not supported on this page. Clicking the "+" button will generate a credit assignment for the entire available
            amount.
          </p>
        </Col>
        <Col sm={12} className="right">
          <span className="margin-right-50">Assign credits as Insetting</span>
          <Switch checked={isInsetting} onClick={(v) => setIsInsetting(v)} />
        </Col>
      </Row>

      {loading ? (
        <PageSpin />
      ) : (
        <AdminTable
          dataSource={farmFieldMethods}
          config={{
            defaultPageSize: 10,
            actions: [
              {
                icon: !loadingCredit ? "klim/plus" : "",
                func: complete,
                tooltip: "Assign these credits",
              },
            ],
            exclude: ["receivedPoints", "moduleId", "assignedCredits", "totalCredits", "methodId", "completed", "fieldId", "id", "farmPartnerId"],
          }}
        />
      )}
    </>
  );
};
